import { Link, useParams } from 'react-router';
import { findStop, saveStop, useConfig } from '../lib/config.ts';
import { usePairsAtSite, useSite } from '../lib/queries.ts';
import { MODE_LABEL, pairKey, SlError, type Pair } from '../lib/sl.ts';
import {
  Card,
  Disclosure,
  Empty,
  ErrorNote,
  LineBadge,
  Spinner,
} from '../components/ui.tsx';
import { MissingLine } from '../components/MissingLine.tsx';

export function StopEditor() {
  const params = useParams();
  const siteId = Number(params.siteId);
  const valid = Number.isFinite(siteId);

  const config = useConfig();
  const saved = findStop(config, siteId);
  const site = useSite(valid ? siteId : null);
  const pairs = usePairsAtSite(valid ? siteId : null);

  // Prefer the name we already stored: it is what you chose this stop by, and
  // it is there instantly, before sites.json has been fetched.
  const name = saved?.name ?? site.data?.name ?? '';
  const note = saved?.note ?? site.data?.note;
  // Which of the ten Ekbackens this is. Worth showing here too, so a wrong
  // turn in search is caught before you tick anything.
  const area = site.data?.area;

  const chosen = new Set((saved?.pairs ?? []).map(pairKey));

  /**
   * Position in the picker's own order -- grouped by mode, then by line
   * number -- so a stop's lines read the same way every time regardless of
   * the order you happened to tick them in.
   *
   * A hand-added line is in no group and gets sorted to the end rather than
   * to the front, which is where an unfound index would otherwise put it.
   */
  const rank = (p: Pair) => {
    const i = pairs.data?.findIndex((q) => pairKey(q) === pairKey(p)) ?? -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  function commit(next: Pair[]) {
    saveStop({
      siteId,
      name: name || `Hållplats ${siteId}`,
      ...(note ? { note } : {}),
      ...(area ? { area } : {}),
      pairs: [...next].sort((a, b) => rank(a) - rank(b)),
    });
  }

  function toggle(pair: Pair) {
    const current = saved?.pairs ?? [];
    const key = pairKey(pair);
    commit(
      chosen.has(key) ? current.filter((p) => pairKey(p) !== key) : [...current, pair],
    );
  }

  function addPair(pair: Pair) {
    if (chosen.has(pairKey(pair))) return;
    commit([...(saved?.pairs ?? []), pair]);
  }

  if (!valid) {
    return <Empty title="Okänd hållplats" />;
  }

  const groups = groupByMode(pairs.data ?? []);

  // Saved pairs the picker cannot show, because SL never listed them.
  const offered = new Set((pairs.data ?? []).map(pairKey));
  const manual = (saved?.pairs ?? []).filter((p) => !offered.has(pairKey(p)));

  return (
    <>
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h1 className="min-w-0 truncate text-2xl">
          {name || 'Hållplats'}
          {(note ?? area) && (
            <span className="text-faint ml-1.5 text-sm font-normal">
              {[note, area].filter(Boolean).join(' · ')}
            </span>
          )}
        </h1>
        <Link
          to="/setup"
          className="text-accent focus-visible:outline-accent shrink-0 rounded-lg text-[15px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Klar
        </Link>
      </header>

      {pairs.isPending ? (
        <Spinner label="Hämtar linjer" />
      ) : (
        <div className="space-y-3">
          {pairs.isError && (
            <Card className="p-4">
              <ErrorNote>
                {/* A quota refusal is not a connection problem, and telling
                    someone to check their connection when it is fine only
                    sends them looking in the wrong place. */}
                {pairs.error instanceof SlError && pairs.error.isQuota
                  ? 'SL:s anropskvot är slut just nu. Försök igen om en stund.'
                  : 'Linjerna kunde inte hämtas. Kontrollera uppkopplingen och försök igen.'}
              </ErrorNote>
            </Card>
          )}

          {!pairs.isError && groups.length === 0 && (
            <Card className="p-4">
              <Empty title="Inga linjer hittades">
                Inget trafikerar den här hållplatsen det närmaste dygnet.
              </Empty>
            </Card>
          )}

          {groups.map(([mode, list]) => (
            <Card key={mode}>
              <Disclosure
                summaryClassName="px-4 py-2.5 border-line group-open:border-b"
                summary={
                  <>
                    <h2 className="text-muted flex-1 text-[13px] font-semibold">
                      {MODE_LABEL[mode] ?? mode}
                    </h2>
                    <span className="text-faint text-[13px]">
                      {list.filter((p) => chosen.has(pairKey(p))).length}/{list.length}
                    </span>
                  </>
                }
              >
                <ul className="divide-line divide-y">
                  {list.map((pair) => {
                    const on = chosen.has(pairKey(pair));
                    return (
                      <li key={pairKey(pair)}>
                        <label className="flex cursor-pointer items-center gap-3 px-4 py-3">
                          <LineBadge line={pair.line} mode={pair.mode} />
                          <span className="min-w-0 flex-1 truncate text-[15px]">
                            {pair.direction}
                          </span>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle(pair)}
                            className="accent-accent size-5 shrink-0"
                          />
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </Disclosure>
            </Card>
          ))}

          {/* Hand-added lines have no row in the groups above -- that is the
              whole reason they exist -- so without this card they could be
              added and never removed. */}
          {manual.length > 0 && (
            <Card>
              <h2 className="text-muted border-line border-b px-4 py-2.5 text-[13px] font-semibold">
                Tillagda manuellt
              </h2>
              <ul className="divide-line divide-y">
                {manual.map((pair) => (
                  <li key={pairKey(pair)}>
                    <label className="flex cursor-pointer items-center gap-3 px-4 py-3">
                      <LineBadge line={pair.line} mode={pair.mode} />
                      <span className="min-w-0 flex-1 truncate text-[15px]">
                        {pair.direction}
                      </span>
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggle(pair)}
                        className="accent-accent size-5 shrink-0"
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <MissingLine siteId={siteId} onAdd={addPair} existing={chosen} />

          {/* Worth saying plainly, because the alternative is quietly
              wondering where a line went. */}
          <p className="text-faint px-1 text-[13px]">
            Listan visar linjer som trafikerar hållplatsen det närmaste dygnet. En linje
            som bara går vissa tider kan saknas här.
          </p>
        </div>
      )}
    </>
  );
}

/** Preserves the order pairsAtSite() sorted into; Map keeps insertion order. */
function groupByMode(pairs: Pair[]): [string, Pair[]][] {
  const groups = new Map<string, Pair[]>();
  for (const p of pairs) {
    const list = groups.get(p.mode);
    if (list) list.push(p);
    else groups.set(p.mode, [p]);
  }
  return [...groups];
}
